import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { DomainNotificationsProviderContext } from '../../internal'
import { GetNotificationResponse } from '@pushly/models/lib/structs/notifications'

type LoadedNotificationProviderApi = {
    isLoading(): boolean
    isLoaded(): boolean
    isEmpty(): boolean
    hasRequestedNotificationId(): boolean
    getRequestedNotificationId(): string | null
    hasSourceTemplateId(): boolean
    getSourceTemplateId(): string | null
    getNotifications(): GetNotificationResponse[]
    getNotificationsPromise(): Promise<GetNotificationResponse[]>
}

export function useLoadedDomainNotifications(): LoadedNotificationProviderApi {
    const ctx = useContext(DomainNotificationsProviderContext)
    if (!ctx) {
        throw new Error('useLoadedDomainNotificationTemplate must be used within a DomainNotificationTemplateProvider')
    }

    const { state } = ctx

    const getNotifications = useCallback(() => {
        return state.notifications
    }, [state.domainId, state.notificationId, state.notifications])

    // workaround for existing use with PromisableProp
    const getNotificationsRef = useRef(getNotifications)
    useEffect(() => {
        getNotificationsRef.current = getNotifications
    }, [getNotifications])

    const loadingRef = useRef(state.loading)
    useEffect(() => {
        loadingRef.current = state.loading
    }, [state.loading])

    const getNotificationsPromiseHasResponded = useRef(false)
    const getNotificationsPromise = useCallback(() => {
        return new Promise<GetNotificationResponse[]>((res) => {
            const fallbackTimer = setTimeout(() => {
                if (getNotificationsPromiseHasResponded.current) return

                console.warn('getNotificationsPromise timed out')

                getNotificationsPromiseHasResponded.current = true
                clearTimeout(fallbackTimer)
                res([])
            }, 30_0000) // max API Gateway response time

            const _getNotifications = () => {
                if (getNotificationsPromiseHasResponded.current) {
                    clearTimeout(fallbackTimer)
                    res(getNotificationsRef.current())
                    return
                }

                if (loadingRef.current) {
                    setTimeout(_getNotifications, 100)
                    return
                }

                getNotificationsPromiseHasResponded.current = true
                loadingRef.current = false
                clearTimeout(fallbackTimer)
                res(getNotificationsRef.current())
            }

            _getNotifications()
        })
    }, [])

    const isLoaded = useCallback(() => !state.loading, [state.loading])
    const isLoading = useCallback(() => state.loading, [state.loading])
    const isEmpty = useCallback(() => !state.notifications, [state.notifications])
    const hasRequestedNotificationId = useCallback(() => !!state.notificationId, [state.notificationId])
    const getRequestedNotificationId = useCallback(() => state.notificationId, [state.notificationId])

    const getSourceTemplateId = useCallback(() => {
        return (
            state.notifications.find((n) => n.template?.source_template_id)?.template?.source_template_id?.toString() ?? null
        )
    }, [state.notifications])
    const hasSourceTemplateId = useCallback(() => {
        return getSourceTemplateId() !== null
    }, [getSourceTemplateId])

    return useMemo(
        () => ({
            isLoaded,
            isLoading,
            isEmpty,
            hasRequestedNotificationId,
            getRequestedNotificationId,
            hasSourceTemplateId,
            getSourceTemplateId,
            getNotifications,
            getNotificationsPromise,
        }),
        [
            isLoaded,
            isLoading,
            isEmpty,
            hasRequestedNotificationId,
            getRequestedNotificationId,
            hasSourceTemplateId,
            getSourceTemplateId,
            getNotifications,
            getNotificationsPromise,
        ],
    )
}
